import * as React from "react";

import { type VariantProps, cva } from "class-variance-authority";
import Swal from "sweetalert2";

import { cn } from "~/lib/utils";

const alertVariants = cva(
  "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export function showAlert({
  title,
  text,
  icon = "info",
  confirmButtonText = "OK",
}: {
  title: string;
  text?: string;
  icon?: "success" | "error" | "warning" | "info" | "question";
  confirmButtonText?: string;
}) {
  return Swal.fire({
    title,
    text,
    icon,
    confirmButtonText,
    confirmButtonColor: "#3085d6",
    customClass: {
      container: "swal2-z-top",
      popup: "z-[100000]",
    },
    // Strong mobile isolation
    allowOutsideClick: false,
    allowEscapeKey: false,
    backdrop: `rgba(0,0,0,0.6)`,
    heightAuto: false,
    scrollbarPadding: false,
    stopKeydownPropagation: true,
    focusConfirm: true,
    reverseButtons: false,
    buttonsStyling: true,
  });
}

export function showConfirm({
  title,
  text,
  icon = "warning",
  confirmButtonText = "Sí",
  cancelButtonText = "No",
}: {
  title: string;
  text?: string;
  icon?: "warning" | "question";
  confirmButtonText?: string;
  cancelButtonText?: string;
}) {
  return Swal.fire({
    title,
    text,
    icon,
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    customClass: {
      container: "swal2-z-top",
      popup: "z-[100000]",
    },
    // Strong mobile isolation
    allowOutsideClick: false,
    allowEscapeKey: false,
    backdrop: `rgba(0,0,0,0.6)`,
    heightAuto: false,
    scrollbarPadding: false,
    stopKeydownPropagation: true,
    focusConfirm: true,
    reverseButtons: false,
    buttonsStyling: true,
  });
}

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertDescription, AlertTitle };
